import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Wallet, CalendarDays, TrendingUp } from "lucide-react";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
const fmt = (n) => Number(n||0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function Dashboard() {
  const [month, setMonth] = useState(currentMonth());
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/dashboard/${month}`);
    setD(data);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-8 space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Snapshot of the selected month.</p>
        </div>
        <div>
          <Label className="text-xs text-slate-500 font-mono uppercase">Month</Label>
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-40" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat icon={Users} label="Employees" value={d?.employees ?? "—"} accent="bg-blue-50 text-blue-700" />
        <Stat icon={Wallet} label="Total Take Home" value={`₹ ${fmt(d?.totals?.take_home)}`} accent="bg-emerald-50 text-emerald-700" />
        <Stat icon={CalendarDays} label="Working Days" value={d?.total_working_days ?? "—"} accent="bg-amber-50 text-amber-700" />
        <Stat icon={TrendingUp} label="Total Earn" value={`₹ ${fmt(d?.totals?.total_earn)}`} accent="bg-violet-50 text-violet-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Deductions & Contributions">
          <Row k="Employee PF" v={fmt(d?.totals?.pf)} />
          <Row k="Employee ESI" v={fmt(d?.totals?.esi)} />
          <Row k="Employer PF" v={fmt(d?.totals?.employer_pf)} />
          <Row k="Employer ESI" v={fmt(d?.totals?.employer_esi)} />
          <Row k="Total Incentive / OT" v={fmt(d?.totals?.total_incentive)} />
        </Card>
        <Card title="Top Earners">
          {(d?.top_earners || []).map((e)=>(
            <div key={e.employee_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <div>
                <div className="font-medium">{e.name}</div>
                <div className="text-xs text-slate-500 font-mono">{e.code}</div>
              </div>
              <div className="font-mono text-emerald-700 font-semibold">₹ {fmt(e.take_home)}</div>
            </div>
          ))}
          {(!d?.top_earners || d.top_earners.length===0) && <div className="text-sm text-slate-400 py-4">No data yet.</div>}
        </Card>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className={`w-9 h-9 grid place-items-center rounded-md ${accent}`}><Icon size={18}/></div>
      <div className="mt-3 text-xs uppercase tracking-wider font-mono text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-heading font-bold text-slate-900">{value}</div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className="font-heading font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-600">{k}</span>
      <span className="font-mono font-medium">₹ {v}</span>
    </div>
  );
}
